import { ScrollView, StyleSheet, Text, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import type { PropsWithChildren } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { Edges } from 'react-native-safe-area-context';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { AuthenticatedRouteTransition } from './AuthenticatedRouteTransition';
import { BrandLogo } from './BrandLogo';

type ScreenProps = PropsWithChildren<{
  contentStyle?: StyleProp<ViewStyle>;
  title?: string;
  subtitle?: string;
  scroll?: boolean;
  showBrand?: boolean;
}>;

const DEFAULT_SAFE_AREA_EDGES: Edges = {
  bottom: 'additive',
  left: 'additive',
  right: 'additive',
  top: 'additive',
};
const FIXED_CONTENT_SAFE_AREA_EDGES: Edges = {
  bottom: 'off',
  left: 'additive',
  right: 'additive',
  top: 'additive',
};

export function Screen({
  contentStyle,
  title,
  subtitle,
  scroll = true,
  showBrand = false,
  children,
}: ScreenProps) {
  const content = (
    <View style={[styles.content, contentStyle]}>
      {showBrand ? <BrandLogo size="lg" /> : null}
      {title ? (
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        </View>
      ) : null}
      {children}
    </View>
  );
  const routeContent = (
    <AuthenticatedRouteTransition>{content}</AuthenticatedRouteTransition>
  );
  const safeAreaEdges = scroll
    ? DEFAULT_SAFE_AREA_EDGES
    : FIXED_CONTENT_SAFE_AREA_EDGES;

  return (
    <SafeAreaView edges={safeAreaEdges} style={styles.safeArea}>
      {scroll ? (
        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={styles.scrollView}
          contentContainerStyle={styles.scroll}
        >
          {routeContent}
        </ScrollView>
      ) : (
        routeContent
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    backgroundColor: colors.background,
    flex: 1,
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  scroll: {
    flexGrow: 1,
    width: '100%',
  },
  content: {
    alignSelf: 'center',
    flex: 1,
    gap: spacing.xl,
    maxWidth: 640,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xl,
    width: '100%',
  },
  header: {
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  title: {
    color: colors.text,
    fontSize: 32,
    fontWeight: '800',
  },
  subtitle: {
    color: colors.mutedText,
    fontSize: 16,
    lineHeight: 22,
  },
});
